// src/utils/api.js
import axiosInstance from '../../../../api/axiosInstance';

// ---------- Mock toggles ----------------------------------------------------
// Turn on mocks by either setting an env var, OR automatically when a request 404s/HTMLs.
// To force on: VITE_USE_MOCKS=true  (Vite)  |  REACT_APP_USE_MOCKS=true (CRA)
let MOCKS_ON =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_USE_MOCKS === 'true') ||
  (typeof process !== 'undefined' && process.env?.REACT_APP_USE_MOCKS === 'true');

// ---------- In-memory mock "DB" (per tab + business) -----------------------
const mem = {
  discounts: new Map(),      // key: `${kind}:${biz}` -> [{...}]
  products: new Map(),       // key: `products:${biz}` -> [{ id,name }]
  offerings: new Map(),      // key: `reservations:${biz}` -> [{ category, items:[] }]
};

// Simple id generator
function uid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0, v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
const keyKindBiz = (kind, businessId) => `${kind}:${businessId ?? 'global'}`;

// Seed demo data once (optional)
function seedIfEmpty(kind, businessId) {
  const k = keyKindBiz(kind, businessId);
  if (!mem.discounts.has(k)) {
    mem.discounts.set(k, []);
  }
  if (!mem.products.has(`products:${businessId ?? 'global'}`)) {
    mem.products.set(`products:${businessId ?? 'global'}`, [
      { id: uid(), name: 'Gift Card 50' },
      { id: uid(), name: 'Yoga Mat Pro' },
      { id: uid(), name: 'Online Course: Intro' },
    ]);
  }
  if (!mem.offerings.has(`reservations:${businessId ?? 'global'}`)) {
    mem.offerings.set(`reservations:${businessId ?? 'global'}`, [
      {
        category: 'services',
        items: [
          { id: uid(), name: 'Haircut 30m' },
          { id: uid(), name: 'Massage 60m' },
          { id: uid(), name: 'Private Lesson' },
        ],
      },
    ]);
  }
}

// Helper to decide if a failure looks like “no backend”
function looksLikeNoBackend(err) {
  const ct = err?.response?.headers?.['content-type'] || '';
  return (
    err?.response?.status === 404 ||
    (typeof ct === 'string' && ct.includes('text/html')) // server sent index.html
  );
}

// ----------------------------------------------------------------------------
// Public API (same function names you already use)
// ----------------------------------------------------------------------------

export async function getDiscounts(kind, businessId) {
  try {
    const res = await axiosInstance.get(`/discounts/discounts/${kind}`, {
      params: { business_id: businessId ?? undefined },
    });
    return res?.data ?? [];
  } catch (err) {
    if (!MOCKS_ON && looksLikeNoBackend(err)) {
      console.warn('[discounts] Backend missing, enabling in-memory mocks.');
      MOCKS_ON = true;
    }
    if (!MOCKS_ON) throw err;

    seedIfEmpty(kind, businessId);
    const list = mem.discounts.get(keyKindBiz(kind, businessId)) || [];
    return structuredClone(list);
  }
}

export async function saveDiscount(kind, body, id = null, businessId) {
  try {
    const url = id ? `/discounts/${kind}/${id}` : `/discounts/${kind}`;
    const method = id ? 'put' : 'post';
    const res = await axiosInstance[method](url, body, {
      params: { business_id: businessId ?? undefined },
    });
    return res?.data ?? null;
  } catch (err) {
    if (!MOCKS_ON && looksLikeNoBackend(err)) {
      console.warn('[discounts] Backend missing, using in-memory mocks for save.');
      MOCKS_ON = true;
    }
    if (!MOCKS_ON) throw err;

    // --- Mock save/update in memory
    seedIfEmpty(kind, businessId);
    const k = keyKindBiz(kind, businessId);
    const list = mem.discounts.get(k);

    if (id) {
      const idx = list.findIndex(x => x.id === id);
      if (idx >= 0) {
        list[idx] = { ...list[idx], ...body, id };
      } else {
        list.push({ ...body, id });
      }
      return structuredClone(list[idx >= 0 ? idx : list.length - 1]);
    } else {
      const newId = uid();
      const row = { ...body, id: newId };
      list.unshift(row);
      return structuredClone(row);
    }
  }
}

export async function deleteDiscount(kind, id, businessId) {
  try {
    await axiosInstance.delete(`/discounts/${kind}/${id}`, {
      params: { business_id: businessId ?? undefined },
    });
    return true;
  } catch (err) {
    if (!MOCKS_ON && looksLikeNoBackend(err)) {
      console.warn('[discounts] Backend missing, using in-memory mocks for delete.');
      MOCKS_ON = true;
    }
    if (!MOCKS_ON) throw err;

    seedIfEmpty(kind, businessId);
    const k = keyKindBiz(kind, businessId);
    const list = mem.discounts.get(k);
    const next = list.filter(x => x.id !== id);
    mem.discounts.set(k, next);
    return true;
  }
}

// Cache promise per kind+biz (optional; safe to keep)
const offeringsCache = new Map();

export async function getOfferings(kind, businessId) {
  const key = `${kind}:${businessId ?? 'global'}`;
  console.log('Fetching offerings for:', key);
  if (offeringsCache.has(key)) return offeringsCache.get(key);
console.log('No cache, fetching from API or mocks...');
  const p = (async () => {
    try {
      const path = kind === 'reservations' ? '/discounts/reservations' : '/products';
      const res = await axiosInstance.get(path, {
        params: { business_id: businessId ?? undefined },
      });
      console.log('Offerings fetched from API:', res.data);
      return res?.data ?? [];
    } catch (err) {
      if (!MOCKS_ON && looksLikeNoBackend(err)) {
        console.warn('[offerings] Backend missing, using in-memory mocks.');
        MOCKS_ON = true;
      }
      if (!MOCKS_ON) throw err;

      seedIfEmpty(kind, businessId);
      if (kind === 'reservations') {
        return structuredClone(mem.offerings.get(`reservations:${businessId ?? 'global'}`) || []);
      } else {
        return structuredClone(mem.products.get(`products:${businessId ?? 'global'}`) || []);
      }
    }
  })();

  offeringsCache.set(key, p);
  return p;
}

export function invalidateOfferings(kind, businessId) {
  offeringsCache.delete(`${kind}:${businessId ?? 'global'}`);
}

export async function getBaseCurrency(businessId) {
  try {
    const { data } = await axiosInstance.get('/store_settings', {
      params: { businessId },
    });
    return data?.payments?.currency || 'USD';
  } catch (err) {
    if (!MOCKS_ON && looksLikeNoBackend(err)) {
      console.warn('[discounts] No store settings backend — assuming USD.');
      MOCKS_ON = true;
    }
    return 'USD';
  }
}